import React, { createContext, useContext, useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Video, X, MessageSquare } from 'lucide-react';

// Lazy-load del chat sidebar para no penalizar el bundle inicial.
const LiveChatSidebar = lazy(() => import('../components/LiveChatSidebar'));

// Context global para la videollamada.
//
// Antes el iframe vivía DENTRO de SalaDocente. Eso funcionaba bien al
// cambiar de pestaña dentro de Sala Docente (chat, kanban, etc.) pero
// si el profesor navegaba a OTRA RUTA — ej. abrir la ficha de un alumno
// desde "Avance de Estudiantes" (/app/estudiante/:id) — React Router
// desmontaba todo SalaDocente y la llamada se cortaba.
//
// Solución: el iframe vive a nivel App.js (siempre montado) y se
// controla desde acá. Navegues donde navegues, el iframe sigue flotando
// o en pantalla completa según el estado de minimizado.
//
// API pública:
//   const { activeCall, isMinimized, openCall, closeCall, toggleMinimize } = useVideoCall();
//   openCall({ url, channelId, liveChat: { channelId, channelType } })
//
// `liveChat`: si se pasa, se renderiza un sidebar con el chat en vivo
// (Stream Chat livestream channel) al estilo YouTube Live.
//
// Cuando se cierra una llamada, se dispara el evento global
// `iba:call-ended` con `detail = { channelId }`. SalaDocente lo escucha
// para enviar el mensaje silencioso "📵 Llamada finalizada" al canal
// (ese paso necesita el Stream chat client, que vive en SalaDocente).

const VideoCallContext = createContext(null);

export const useVideoCall = () => {
  const ctx = useContext(VideoCallContext);
  if (!ctx) {
    throw new Error('useVideoCall must be used within VideoCallProvider');
  }
  return ctx;
};

export const VideoCallProvider = ({ children }) => {
  const [activeCall, setActiveCall] = useState(null); // { url, channelId, liveChat }
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);

  // Al cerrar la llamada, resetear minimizado
  useEffect(() => {
    if (!activeCall) {
      setIsMinimized(false);
      setChatOpen(true);
    }
  }, [activeCall]);

  const openCall = useCallback(({ url, channelId, liveChat }) => {
    if (!url) return;
    setActiveCall({ url, channelId: channelId || null, liveChat: liveChat || null });
    setChatOpen(true);
  }, []);

  const closeCall = useCallback(() => {
    setActiveCall((prev) => {
      if (prev) {
        try {
          window.dispatchEvent(
            new CustomEvent('iba:call-ended', { detail: { channelId: prev.channelId } })
          );
        } catch (_) { /* no-op */ }
      }
      return null;
    });
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((m) => !m);
  }, []);

  const hasLiveChat = !!(activeCall?.liveChat?.channelId);
  const showChat = hasLiveChat && chatOpen && !isMinimized;

  return (
    <VideoCallContext.Provider value={{ activeCall, isMinimized, openCall, closeCall, toggleMinimize }}>
      {children}
      {activeCall && (
        <div
          className={
            isMinimized
              ? 'fixed bottom-4 right-4 z-[9998] bg-black rounded-2xl shadow-2xl border-2 border-emerald-500 flex flex-col overflow-hidden w-[420px] h-[280px]'
              : 'fixed inset-0 z-[9998] bg-black/85 flex flex-col'
          }
          style={
            isMinimized
              ? { resize: 'both', overflow: 'hidden' }
              : { height: '100dvh', maxHeight: '100dvh' }
          }
          data-testid="global-active-call-iframe"
        >
          <div
            className={`flex items-center justify-between bg-black/70 shrink-0 ${
              isMinimized ? 'px-2 py-1.5' : 'p-3 bg-black/40'
            }`}
          >
            <div
              className={`text-white font-semibold flex items-center gap-2 ${
                isMinimized ? 'text-xs' : 'text-base'
              }`}
            >
              <Video className={isMinimized ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
              {isMinimized ? 'Llamada' : (hasLiveChat ? 'Transmisión en vivo' : 'Videollamada')}
              {(isMinimized || hasLiveChat) && (
                <span
                  className="ml-1 inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"
                  title="En vivo"
                />
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {hasLiveChat && !isMinimized && (
                <button
                  onClick={() => setChatOpen((v) => !v)}
                  className="bg-white/15 hover:bg-white/25 text-white rounded-full font-semibold flex items-center gap-1 px-3 py-1.5 text-sm"
                  title={chatOpen ? 'Ocultar chat' : 'Mostrar chat'}
                  data-testid="global-toggle-chat-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline">{chatOpen ? 'Ocultar chat' : 'Mostrar chat'}</span>
                </button>
              )}
              <button
                onClick={toggleMinimize}
                className={`bg-white/15 hover:bg-white/25 text-white rounded-full font-semibold flex items-center gap-1 ${
                  isMinimized ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                }`}
                title={isMinimized ? 'Expandir a pantalla completa' : 'Minimizar (ver otras pantallas)'}
                data-testid={isMinimized ? 'global-expand-call-btn' : 'global-minimize-call-btn'}
              >
                <i className={`fas ${isMinimized ? 'fa-expand' : 'fa-compress'}`} />
                {!isMinimized && <span>Minimizar</span>}
              </button>
              <button
                onClick={closeCall}
                className={`bg-red-600 hover:bg-red-700 text-white rounded-full font-semibold flex items-center gap-1 ${
                  isMinimized ? 'px-2 py-1 text-xs' : 'px-4 py-1.5 text-sm'
                }`}
                data-testid="global-end-call-btn"
              >
                <X className={isMinimized ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                {!isMinimized && <span>Finalizar</span>}
              </button>
            </div>
          </div>
          {/* Layout: video + chat opcional */}
          <div className="flex-1 flex flex-col md:flex-row min-h-0">
            <iframe
              title="Videollamada"
              src={activeCall.url}
              allow="camera; microphone; fullscreen; speaker; display-capture; autoplay"
              allowFullScreen
              className={`w-full border-0 ${
                showChat ? 'md:flex-1 flex-1 md:h-auto h-1/2 md:min-h-0' : 'flex-1'
              }`}
              style={{ minHeight: 0 }}
            />
            {showChat && (
              <div className="bg-white border-l border-gray-200 flex flex-col md:w-[360px] w-full md:h-auto h-1/2 md:max-h-full min-h-0">
                <Suspense fallback={
                  <div className="flex-1 flex items-center justify-center text-xs text-gray-400">
                    Cargando chat...
                  </div>
                }>
                  <LiveChatSidebar
                    channelId={activeCall.liveChat.channelId}
                    channelType={activeCall.liveChat.channelType || 'livestream'}
                  />
                </Suspense>
              </div>
            )}
          </div>
        </div>
      )}
    </VideoCallContext.Provider>
  );
};
